import type { ReactNode } from 'react';
import { Button } from '@mui/material';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded';
import { ApiError } from '@/api';
import { useAuth } from '@/auth';
import { SplashScreen } from '@/components/SplashScreen';
import { StatusScreen } from '@/components/StatusScreen';
import { useTenant } from '@/tenant/useTenant';
import { useSession } from './useSession';

/**
 * Loads /me after sign-in and turns authorization failures into clear screens:
 * suspended space, account without access, API down. Children only render
 * with a valid session for THIS space.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const { signOut, user } = useAuth();
  const { displayName, isPlatform, isMarketplace } = useTenant();

  if (session.isPending) return <SplashScreen />;
  if (session.isError) {
    const code = session.error instanceof ApiError ? session.error.code : null;
    const signOutButton = (
      <Button variant="outlined" onClick={() => void signOut()}>
        Sair e entrar com outra conta
      </Button>
    );
    if (code === 'tenant-suspended') {
      return (
        <StatusScreen
          icon={<PauseCircleOutlineRounded color="warning" sx={{ fontSize: 48 }} />}
          title="Espaço temporariamente indisponível"
          description={`O acesso a ${displayName} está pausado no momento. Fale com o responsável pelo espaço.`}
          actions={signOutButton}
        />
      );
    }
    if (isMarketplace && (code === 'no-access' || code === 'user-disabled')) {
      return (
        <StatusScreen
          icon={<BlockRounded color="error" sx={{ fontSize: 48 }} />}
          title="Conta suspensa"
          description={`A conta ${user?.email ?? ''} está suspensa em ${displayName} depois de denúncias de outras pessoas. Se acha que foi um engano, fale com o suporte.`}
          actions={signOutButton}
        />
      );
    }
    if (code === 'no-access' || code === 'user-disabled') {
      return (
        <StatusScreen
          icon={<BlockRounded color="error" sx={{ fontSize: 48 }} />}
          title="Sem acesso a este espaço"
          description={
            isPlatform
              ? `A conta ${user?.email ?? ''} não opera a plataforma. Se você é cliente de um profissional, acesse pelo endereço que ele enviou.`
              : `A conta ${user?.email ?? ''} ainda não foi convidada para ${displayName}. Peça um convite e entre com o mesmo e-mail.`
          }
          actions={signOutButton}
        />
      );
    }
    return (
      <StatusScreen
        icon={<CloudOffRounded color="error" sx={{ fontSize: 48 }} />}
        title="Não foi possível carregar sua sessão"
        description="Verifique sua conexão e tente novamente."
        actions={
          <>
            <Button variant="contained" onClick={() => void session.refetch()}>
              Tentar de novo
            </Button>
            {signOutButton}
          </>
        }
      />
    );
  }
  return children;
}
